import type {
  GenericSearchParam,
  ProfileSearchParams,
  SearchParamWithImage,
} from "common/types";

import { DEFAULT_SEARCH_SOURCE_PARAMS } from "../constants";

type SearchParamValue = GenericSearchParam | SearchParamWithImage;
type MultiSelectKey = keyof Omit<
  ProfileSearchParams,
  "keywords" | "followers_of"
>;

export default function useSearchSourceParams(
  searchParams: ProfileSearchParams,
  setSearchParams: (searchParams: Partial<ProfileSearchParams>) => void,
) {
  function addSearchParamValue(key: MultiSelectKey, value: SearchParamValue) {
    setSearchParams({ [key]: [...searchParams[key], value] });
  }

  // only the urn is needed to remove a value
  function removeSearchParamValue(
    key: MultiSelectKey,
    { urn: removedUrn }: { urn: string },
  ) {
    setSearchParams({
      [key]: searchParams[key].filter(({ urn }) => urn !== removedUrn),
    });
  }

  function setSearchParam<T extends keyof ProfileSearchParams>(
    key: T,
    value: ProfileSearchParams[T],
  ) {
    setSearchParams({ [key]: value });
  }

  function resetFilters() {
    setSearchParams({
      ...DEFAULT_SEARCH_SOURCE_PARAMS,
      keywords: searchParams.keywords,
    });
  }

  function resetParams() {
    setSearchParams(DEFAULT_SEARCH_SOURCE_PARAMS);
  }

  //the keywords have their own input, so they don't count as a filter
  const { keywords: _, ...filters } = searchParams;
  const hasFilters = Object.values(filters).some((filter) =>
    Array.isArray(filter) ? !!filter.length : !!filter,
  );

  return {
    addSearchParamValue,
    removeSearchParamValue,
    setSearchParam,
    resetFilters,
    resetParams,
    hasFilters,
  };
}
